import { useEffect, useState } from 'preact/hooks';
import { isDesktop } from 'react-device-detect';

import appleWalletLogo from '@/assets/images/apple-wallet.svg';
import googleWalletLogo from '@/assets/images/google-wallet.svg';
import walletPassesLogo from '@/assets/images/wallet-passes.svg';
import { BottomSheet } from '@/components/BottomSheet';
import { Spinner } from '@/components/Spinner';
import { useLoyaltyProgramContext } from '@/context/LoyaltyProgramContext';
import { usePlatformHandlers } from '@/context/PlatformContext';
import type {
  T_ConfigureWalletPassURLBodyParams,
  T_ConfigureWalletPassURLResponse,
} from '@/types/Shopify';
import type { I_WalletButtonsProps } from '@/types/Wallet';
import { E_WalletApp } from '@/types/Wallet';

import { ScanQR } from './ScanQR';
import { WalletPassButton } from './WalletPassButton';

export default function WalletButtons({ onClick }: I_WalletButtonsProps) {
  const [selectedPassApplication, setSelectedPassApplication] = useState<E_WalletApp>();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isApple,
    enableWalletAvailable,
    googleWalletAvailable,
    walletPassesAvailable,
    brandName,
    setQrCodeUrl,
  } = useLoyaltyProgramContext();
  const { configureWalletPassURL } = usePlatformHandlers();

  const walletButtonsConfig = [
    {
      logo: appleWalletLogo,
      text: 'Add to Apple Wallet',
      passApplication: E_WalletApp.APPLE_WALLET,
      visible: isApple,
    },
    {
      text: `Add ${brandName} Card`,
      passApplication: E_WalletApp.ENABLE_WALLET,
      visible: enableWalletAvailable,
    },
    {
      logo: googleWalletLogo,
      text: 'Add to Google Wallet',
      passApplication: E_WalletApp.GOOGLE_WALLET,
      visible: googleWalletAvailable,
    },
    {
      logo: walletPassesLogo,
      text: 'Add to Passbook® Wallet',
      passApplication: E_WalletApp.WALLET_PASSES,
      visible: walletPassesAvailable,
    },
  ];

  const visibleButtons = walletButtonsConfig.filter(({ visible }) => visible);

  useEffect(() => {
    if (selectedPassApplication && onClick) {
      onClick();
    }
  }, [selectedPassApplication, onClick]);

  const handleRegister = async (
    registrationData: T_ConfigureWalletPassURLBodyParams,
  ): Promise<T_ConfigureWalletPassURLResponse | null> => {
    setIsLoading(true);
    try {
      if (!configureWalletPassURL) return null;

      const result = await configureWalletPassURL(registrationData);
      setQrCodeUrl(result?.data?.passLink);
      return result;
    } catch (err) {
      console.error('error registering the customer', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {visibleButtons.map(({ logo, text, passApplication }, index) => (
        <WalletPassButton
          key={passApplication}
          logo={logo}
          text={{ firstLine: text }}
          disabled={isLoading}
          onClick={async () => {
            setSelectedPassApplication(passApplication);
            if (isDesktop) {
              setIsOpen(true);
            }

            const data = await handleRegister({
              walletApp: passApplication,
            });

            const passLink = data?.data?.passLink;

            if (!isDesktop && passLink) {
              window.location.href = passLink;
            }
          }}
          filled={!isDesktop && index === 0}
        />
      ))}

      <BottomSheet
        disclosure={{
          isOpen,
          onClose: () => setIsOpen(false),
        }}
      >
        {isLoading ? <Spinner /> : <ScanQR selectedPassApplication={selectedPassApplication} />}
      </BottomSheet>
    </>
  );
}
